/**
 * Allys Contact Information Section Component
 * 
 * Displays contact information in a modern, card-based layout with Allys theming
 */

'use client';

import { memo } from 'react';
import { ContactInformationSection, type ContactInfo } from '@/components/contact/ContactInformationSection';
import { Envelope, Phone, MapPin, Clock } from '@phosphor-icons/react';

// Helper to safely get string values from messages
const safeGetString = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string') return value;
  return fallback;
};

// Helper to safely get object values
const safeGetObject = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'object' && value !== null) return value as Record<string, unknown>;
  return {};
};

export interface AllysContactInformationSectionProps {
  messages?: Record<string, unknown>;
  locale?: string;
}

export const AllysContactInformationSection = memo(function AllysContactInformationSection({
  messages,
  locale: _locale
}: AllysContactInformationSectionProps) {
  const contactMessages = messages?.contact as Record<string, unknown> | undefined;
  const infoMessages = safeGetObject(contactMessages?.info);

  const contactInfo: ContactInfo[] = [
    {
      type: 'email',
      label: safeGetString(infoMessages?.emailLabel, 'Email'),
      value: safeGetString(infoMessages?.email, 'contact@allys.mu'),
      icon: <Envelope size={24} weight="light" />,
      href: `mailto:${safeGetString(infoMessages?.email, 'contact@allys.mu')}`,
    },
    {
      type: 'phone',
      label: safeGetString(infoMessages?.phoneLabel, 'Phone'),
      value: safeGetString(infoMessages?.phone, '+230 460 0909'),
      icon: <Phone size={24} weight="light" />,
      href: `tel:${safeGetString(infoMessages?.phone, '+230 460 0909')}`,
    },
    {
      type: 'address',
      label: safeGetString(infoMessages?.addressLabel, 'Address'),
      value: safeGetString(infoMessages?.address, 'Beau Bassin-Rose Hill, Mauritius'),
      icon: <MapPin size={24} weight="light" />,
    },
    {
      type: 'hours',
      label: safeGetString(infoMessages?.hours, 'Office Hours'),
      value: `${safeGetString(infoMessages?.hoursValue, 'Monday - Friday: 9:00 AM - 5:00 PM')}
${safeGetString(infoMessages?.hoursSaturday, 'Saturday: 9:00 AM - 1:00 PM')}`,
      icon: <Clock size={24} weight="light" />,
    },
  ];

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ContactInformationSection
          title={safeGetString(infoMessages?.title, 'Contact Information')}
          subtitle={safeGetString(infoMessages?.subtitle, 'Our office is located in the heart of Mauritius. Visit us or reach out anytime.')}
          contactInfo={contactInfo}
          className="allys-contact-information"
          renderContactItem={(info) => (
            <div
              key={`${info.type}-${info.label}`}
              className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-200 hover:border-[var(--color-primary)]/30 hover:shadow-xl transition-all duration-300 group"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-accent)] flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300">
                  {info.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2 font-inter"
                  >
                    {info.label}
                  </p>
                  {info.href ? (
                    <a
                      href={info.href}
                      className="text-lg font-medium text-gray-900 hover:text-[var(--color-primary)] transition-colors duration-200 break-words font-inter"
                    >
                      {info.value}
                    </a>
                  ) : (
                    <p
                      className="text-lg font-medium text-gray-900 whitespace-pre-line break-words font-inter"
                    >
                      {info.value}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        />

        <style jsx global>{`
          .allys-contact-information .contact-information-header {
            text-align: center;
            margin-bottom: 4rem;
          }

          .allys-contact-information .contact-information-title {
            font-size: 2.25rem;
            font-weight: 700;
            color: #111827;
            margin-bottom: 1rem;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }

          .allys-contact-information .contact-information-subtitle {
            font-size: 1.125rem;
            color: #6B7280;
            max-width: 42rem;
            margin: 0 auto;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }

          .allys-contact-information .contact-information-grid {
            display: grid;
            grid-template-columns: repeat(1, minmax(0, 1fr));
            gap: 2rem;
          }

          @media (min-width: 640px) {
            .allys-contact-information .contact-information-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }
          }

          @media (min-width: 1024px) {
            .allys-contact-information .contact-information-grid {
              grid-template-columns: repeat(4, minmax(0, 1fr));
            }
          }

          @media (max-width: 640px) {
            .allys-contact-information .contact-information-title {
              font-size: 1.875rem;
            }
          }
        `}</style>
      </div>
    </section>
  );
});

